/**
 * tz.ts — Dependency-free, DST-correct timezone utilities for the admin SPA.
 *
 * Uses only the browser's built-in `Intl.DateTimeFormat` with `formatToParts`
 * to derive zone offsets. No external library; runs on Cloudflare Pages (V8
 * isolate) and in jsdom (Node 22+). All ICU data for IANA zones is present in
 * modern browsers.
 *
 * Key design:
 *   - `wallToUnix(localStr, tz)` → unix seconds (number)
 *     Converts a datetime-local string ("YYYY-MM-DDTHH:mm") in the given IANA
 *     zone to a UTC unix timestamp. DST-correct: we parse the wall-clock time,
 *     anchor to UTC, ask Intl what the zone's wall-clock renders as at that
 *     instant, then adjust the delta iteratively (handles the ±1h DST fold).
 *   - `unixToWall(unixSec, tz)` → "YYYY-MM-DDTHH:mm"
 *     Converts a UTC unix timestamp back to a wall-clock datetime-local string
 *     in the given IANA zone, for display and input seeding.
 *   - `unixToDisplay(unixSec, tz)` → human-readable string
 *     Returns a readable date+time string with the zone abbreviation.
 */

// ── Helpers ────────────────────────────────────────────────────────────────────

/** Extract numeric parts from Intl formatToParts in a given IANA zone. */
function getPartsInZone(
  date: Date,
  tz: string,
): { year: number; month: number; day: number; hour: number; minute: number; second: number } {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  const parts: Record<string, number> = {};
  for (const part of fmt.formatToParts(date)) {
    if (part.type !== 'literal') {
      // hour12:false renders midnight as "24" in some locales; normalise to 0.
      parts[part.type] = part.value === '24' ? 0 : Number(part.value);
    }
  }

  return {
    year: parts['year']!,
    month: parts['month']!,
    day: parts['day']!,
    hour: parts['hour']!,
    minute: parts['minute']!,
    second: parts['second'] ?? 0,
  };
}

/** Parse a datetime-local string ("YYYY-MM-DDTHH:mm" or "YYYY-MM-DDTHH:mm:ss")
 *  into its numeric components. Returns null if the string is not parseable. */
function parseDtLocal(
  dtStr: string,
): { year: number; month: number; day: number; hour: number; minute: number; second: number } | null {
  // datetime-local format: "YYYY-MM-DDTHH:mm" or "YYYY-MM-DDTHH:mm:ss"
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(dtStr);
  if (!m) return null;
  return {
    year: Number(m[1]),
    month: Number(m[2]),
    day: Number(m[3]),
    hour: Number(m[4]),
    minute: Number(m[5]),
    second: m[6] ? Number(m[6]) : 0,
  };
}

/** Format a set of numeric date-time parts back to a datetime-local string. */
function toDatetimeLocal(p: { year: number; month: number; day: number; hour: number; minute: number }): string {
  const pad = (n: number, w = 2) => String(n).padStart(w, '0');
  return `${pad(p.year, 4)}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}

// ── Public API ─────────────────────────────────────────────────────────────────

/**
 * Convert a wall-clock datetime string in the given IANA timezone to a UTC
 * unix timestamp (seconds).
 *
 * Algorithm (iterative offset correction, handles DST folds):
 * 1. Parse the wall-clock string.
 * 2. Treat it as UTC to get a first-guess Date.
 * 3. Ask Intl what the zone renders at that instant → get zone wall-clock.
 * 4. Compute the delta between intended wall-clock and zone wall-clock (in ms).
 * 5. Apply the delta to the guess and repeat once more to handle the fold edge.
 *
 * @param dtLocalStr - "YYYY-MM-DDTHH:mm" (as produced by datetime-local inputs)
 * @param tz - IANA timezone name (e.g. "America/New_York", "UTC")
 * @returns Unix timestamp in seconds, or NaN if dtLocalStr is unparseable.
 */
export function wallToUnix(dtLocalStr: string, tz: string): number {
  const parsed = parseDtLocal(dtLocalStr);
  if (!parsed) return NaN;

  const { year, month, day, hour, minute, second } = parsed;

  // Step 1: Treat the wall-clock as UTC to get a naïve Date (milliseconds).
  const naiveMs = Date.UTC(year, month - 1, day, hour, minute, second, 0);
  let guessDate = new Date(naiveMs);

  // Steps 2–5: Two rounds of correction are sufficient for all real-world DST
  // transitions (offset changes are at most ±1h, a single correction converges).
  for (let i = 0; i < 2; i++) {
    const zoned = getPartsInZone(guessDate, tz);
    // Delta in ms between the zone's wall-clock reading and the intended wall-clock.
    const zonedMs = Date.UTC(
      zoned.year,
      zoned.month - 1,
      zoned.day,
      zoned.hour,
      zoned.minute,
      zoned.second,
      0,
    );
    const deltaMs = naiveMs - zonedMs;
    guessDate = new Date(guessDate.getTime() + deltaMs);
  }

  return Math.floor(guessDate.getTime() / 1000);
}

/**
 * Convert a UTC unix timestamp (seconds) to a datetime-local string ("YYYY-MM-DDTHH:mm")
 * in the given IANA timezone. Suitable for seeding a `datetime-local` input.
 *
 * @param unixSec - UTC unix timestamp in seconds
 * @param tz - IANA timezone name
 * @returns "YYYY-MM-DDTHH:mm" in the given timezone
 */
export function unixToWall(unixSec: number, tz: string): string {
  const date = new Date(unixSec * 1000);
  const p = getPartsInZone(date, tz);
  return toDatetimeLocal(p);
}

/**
 * Format a UTC unix timestamp as a human-readable date+time string in the
 * given IANA timezone, including the timezone abbreviation (e.g. "EST", "PDT").
 *
 * @param unixSec - UTC unix timestamp in seconds
 * @param tz - IANA timezone name
 * @returns Readable string like "Jun 1, 2026, 06:00:00 AM EDT"
 */
export function unixToDisplay(unixSec: number, tz: string): string {
  const date = new Date(unixSec * 1000);
  return new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  }).format(date);
}

// ── Zone list ──────────────────────────────────────────────────────────────────

/** The curated list of IANA timezone options offered in the filter UI. */
export interface TzOption {
  value: string;  // IANA zone name
  label: string;  // display label
}

export const TIMEZONE_OPTIONS: TzOption[] = [
  { value: 'UTC',                  label: 'UTC (Coordinated Universal Time)' },
  { value: 'America/New_York',     label: 'America/New_York (Eastern)' },
  { value: 'America/Chicago',      label: 'America/Chicago (Central)' },
  { value: 'America/Denver',       label: 'America/Denver (Mountain)' },
  { value: 'America/Los_Angeles',  label: 'America/Los_Angeles (Pacific)' },
  { value: 'Europe/London',        label: 'Europe/London (GMT/BST)' },
  { value: 'Europe/Paris',         label: 'Europe/Paris (CET/CEST)' },
  { value: 'Asia/Tokyo',           label: 'Asia/Tokyo (JST)' },
];

/**
 * Return the browser's local IANA zone name (e.g. "America/Los_Angeles"),
 * or "UTC" as a fallback if the Intl API is unavailable.
 */
export function browserTz(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

// ── Current-month helpers ──────────────────────────────────────────────────────

/**
 * Return `[sinceUnix, untilUnix]` for the current month in UTC:
 *   since = 00:00:00 UTC on the first of the current month
 *   until = Date.now() (current moment) in seconds
 *
 * Both values are unix seconds (integers).
 */
export function currentMonthDefaultRange(): [number, number] {
  const now = new Date();
  const startOfMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1, 0, 0, 0, 0));
  const sinceUnix = Math.floor(startOfMonth.getTime() / 1000);
  const untilUnix = Math.floor(now.getTime() / 1000);
  return [sinceUnix, untilUnix];
}
